import classNames from 'classnames'
import endsWith from 'lodash/endsWith'
import find from 'lodash/find'
import isNumber from 'lodash/isNumber'
import some from 'lodash/some'
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

// Generic renderers for the backend's shared tab shape (services/booking-funnel.service.ts):
// { kpis, funnels, charts, tables, notes }. Labels are i18n keys under `bookingFunnel.*`.

const COLORS = ['#fe5579', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#64748b']

const num = value => (value == null ? '—' : Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 }))

// Translate a known bucket/label key, otherwise show the raw value (query text, company name, …).
const label = (t, value) => t(`bookingFunnel.labels.${value}`, { defaultValue: value == null || value === '' ? '—' : String(value) })

// 169 → "2 h 49 min", 5201 → "3 days 14 h".
const duration = (t, minutes) => {
  const m = Math.round(minutes)
  const unit = key => t(`bookingFunnel.units.${key}`)
  if (m < 60) return `${m} ${unit('minutes')}`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} ${unit('hours')} ${m % 60} ${unit('minutes')}`
  return `${Math.floor(h / 24)} ${unit('days')} ${h % 24} ${unit('hours')}`
}

const formatKpi = (t, { value, format }) => {
  if (value == null) return '—'
  if (format === 'text') return label(t, value)
  if (format === 'percent') return `${num(value)}%`
  if (format === 'minutes') return duration(t, value)
  if (format === 'days') return `${num(value)} ${t('bookingFunnel.units.days')}`
  return num(value)
}

// Column convention: `*Rate` columns are percentages, `*Minutes` are durations, `name` is a label.
const formatCell = (t, key, value) => {
  if (key === 'name' || key === 'segment' || key === 'signal') return label(t, value)
  if (value == null) return '—'
  if (endsWith(key, 'Rate')) return `${num(value)}%`
  if (endsWith(key, 'Minutes')) return duration(t, value)
  return isNumber(value) ? num(value) : String(value)
}

const Delta = ({ value }) => {
  if (value == null) return null
  const up = value >= 0
  return (
    <span className={classNames('text-xs font-semibold', up ? 'text-emerald-600' : 'text-rose-600')}>
      {up ? '▲' : '▼'} {Math.abs(value)}%
    </span>
  )
}

const Card = ({ title, children, className }) => (
  <div className={classNames('rounded-xl border border-slate-200 bg-white p-5 shadow-sm', className)}>
    {title ? <h3 className="mb-4 text-base font-semibold text-slate-900">{title}</h3> : null}
    {children}
  </div>
)

export const Notes = ({ notes = [], ga4Error, t }) =>
  notes.length ? (
    <div className="space-y-2">
      {notes.map(note => (
        <div key={note} className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-2 text-sm text-blue-800">
          {t(`bookingFunnel.notes.${note}`)}
          {note === 'ga4Unavailable' && ga4Error ? <span className="block text-xs text-blue-600">{ga4Error}</span> : null}
        </div>
      ))}
    </div>
  ) : null

export const KpiRow = ({ kpis = [], t }) =>
  kpis.length ? (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
      {kpis.map(kpi => (
        <div key={kpi.key} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t(`bookingFunnel.kpis.${kpi.key}`)}</p>
          <p className="mt-2 text-2xl font-bold text-slate-900">{formatKpi(t, kpi)}</p>
          <div className="mt-1 h-4">
            <Delta value={kpi.delta} />
            {kpi.delta != null ? <span className="mx-1 text-xs text-slate-400">{t('bookingFunnel.vsPrevious')}</span> : null}
          </div>
        </div>
      ))}
    </div>
  ) : null

export const FunnelCard = ({ funnel, t }) => {
  const max = Math.max(1, ...funnel.stages.map(s => s.count || 0))
  const bottleneck = find(funnel.stages, 'isBottleneck')
  return (
    <Card title={t(`bookingFunnel.funnels.${funnel.key}`)}>
      {bottleneck ? (
        <p className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {t('bookingFunnel.bottleneckAt', { stage: t(`bookingFunnel.stages.${bottleneck.key}`), rate: bottleneck.stepRate })}
        </p>
      ) : null}
      <div className="space-y-2">
        {funnel.stages.map((stage, i) => (
          // The count sits in its own column, never inside the bar, so short bars can't clip it.
          <div key={stage.key} className="flex items-center gap-3 text-sm">
            <div className="w-24 shrink-0 truncate text-slate-700 sm:w-40">{t(`bookingFunnel.stages.${stage.key}`)}</div>
            <div className="h-7 min-w-0 flex-1 rounded bg-slate-100">
              <div
                className={classNames('h-7 rounded', stage.isBottleneck ? 'bg-rose-500' : 'bg-blue-500')}
                style={{ width: `${stage.count ? Math.max(1, (stage.count / max) * 100) : 0}%` }}
              />
            </div>
            <div className={classNames('w-16 shrink-0 text-right font-semibold tabular-nums', stage.isBottleneck ? 'text-rose-600' : 'text-slate-900')}>{num(stage.count)}</div>
            <div className="flex w-24 shrink-0 flex-col items-end">
              {i > 0 ? (
                <span className={classNames('font-semibold tabular-nums', stage.isBottleneck ? 'text-rose-600' : 'text-slate-700')}>
                  {stage.stepRate == null ? '—' : `${stage.stepRate}%`}
                </span>
              ) : null}
              {stage.isBottleneck ? (
                <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-rose-700">{t('bookingFunnel.bottleneck')}</span>
              ) : null}
              <Delta value={stage.delta} />
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-400">{t('bookingFunnel.stepRateHint')}</p>
    </Card>
  )
}

export const ChartCard = ({ chart, t }) => {
  const data = chart.type === 'bar' ? chart.data.map(row => ({ ...row, [chart.xKey]: label(t, row[chart.xKey]) })) : chart.data
  const series = chart.series.map((key, i) => ({ key, name: t(`bookingFunnel.series.${key}`), color: COLORS[i % COLORS.length] }))
  const Chart = chart.type === 'bar' ? BarChart : LineChart
  // Zero-filled buckets (e.g. lead time) still count as empty.
  const hasData = some(chart.data, row => some(chart.series, key => Number(row[key]) > 0))
  return (
    <Card title={t(`bookingFunnel.sections.${chart.key}`)}>
      {hasData ? (
        <ResponsiveContainer width="100%" height={280}>
          <Chart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey={chart.xKey} stroke="#666" tick={{ fontSize: 11 }} />
            <YAxis stroke="#666" allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            {series.length > 1 ? <Legend /> : null}
            {series.map(s =>
              chart.type === 'bar' ? (
                <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} />
              ) : (
                <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2} dot={false} />
              )
            )}
          </Chart>
        </ResponsiveContainer>
      ) : (
        <p className="py-10 text-center text-sm text-slate-400">{t('bookingFunnel.noData')}</p>
      )}
    </Card>
  )
}

export const DataTable = ({ table, t }) => (
  <Card title={t(`bookingFunnel.sections.${table.key}`)} className="overflow-hidden">
    {table.rows.length ? (
      <div className="-mx-5 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              {table.columns.map(col => (
                <th key={col} className="whitespace-nowrap px-4 py-2 text-left rtl:text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t(`bookingFunnel.cols.${col}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {table.rows.map((row, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i} className="hover:bg-slate-50">
                {table.columns.map(col => (
                  <td key={col} className="whitespace-nowrap px-4 py-2 text-slate-700">
                    {formatCell(t, col, row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <p className="py-6 text-center text-sm text-slate-400">{t('bookingFunnel.noData')}</p>
    )}
  </Card>
)

export const TabReport = ({ report, t }) => (
  <div className="space-y-6">
    <Notes notes={report.notes} ga4Error={report.ga4Error} t={t} />
    <KpiRow kpis={report.kpis} t={t} />
    {report.funnels.length ? (
      <div className={classNames('grid gap-6', report.funnels.length > 1 && 'xl:grid-cols-2')}>
        {report.funnels.map(f => (
          <FunnelCard key={f.key} funnel={f} t={t} />
        ))}
      </div>
    ) : null}
    {report.charts.length ? (
      <div className="grid gap-6 xl:grid-cols-2">
        {report.charts.map(c => (
          <ChartCard key={c.key} chart={c} t={t} />
        ))}
      </div>
    ) : null}
    {report.tables.map(table => (
      <DataTable key={table.key} table={table} t={t} />
    ))}
  </div>
)
